import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "exchange-rate" }
const _hoisted_2 = { class: "er-header" }
const _hoisted_3 = { class: "er-header__right" }
const _hoisted_4 = { class: "er-main" }
const _hoisted_5 = { class: "er-card" }
const _hoisted_6 = { class: "er-form" }
const _hoisted_7 = { class: "er-form__date" }
const _hoisted_8 = { class: "er-form__rows" }
const _hoisted_9 = { class: "er-form__eq" }
const _hoisted_10 = { class: "er-form__actions" }
const _hoisted_11 = { class: "er-card" }
const _hoisted_12 = { class: "er-card__head" }
const _hoisted_13 = { class: "er-card__filter" }
const _hoisted_14 = { class: "er-rate" }

import { computed, onMounted, ref } from "vue";
import { ElMessage } from "element-plus";
import { Delete, Plus } from "@element-plus/icons-vue";
import {
  CurrencyOption,
  ExchangeRateItem,
  deleteExchangeRateApi,
  getCurrenciesApi,
  getEffectiveRatesApi,
  getExchangeRateListApi,
  saveExchangeRateApi,
} from "@/api/exchangeRateApi";

/** 录入行 */
interface FormRow {
  currency: string;
  rate: number | undefined;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ExchangeRateView',
  setup(__props) {

const currencyOptions = ref<CurrencyOption[]>([]);
const effective = ref<{ date: string; rates: Record<string, number> } | null>(
  null
);
const list = ref<ExchangeRateItem[]>([]);
const listLoading = ref(false);
const saving = ref(false);

const formDate = ref<string>(today());
const formRows = ref<FormRow[]>([]);
const filterCurrency = ref<string>("");

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

const disableFuture = (d: Date) => d.getTime() > Date.now();

/** 有效行（币种与汇率都已填） */
const validRows = computed(() =>
  formRows.value.filter((r) => r.currency && r.rate !== undefined && r.rate > 0)
);

/** 该币种是否已被其他行选中（避免同版重复） */
const isCurrencyUsed = (code: string, exceptIdx: number) =>
  formRows.value.some((r, i) => i !== exceptIdx && r.currency === code);

const addRow = (preset?: { currency: string; rate?: number }) => {
  formRows.value.push({
    currency: preset?.currency ?? "",
    rate: preset?.rate,
  });
};

const removeRow = (i: number) => {
  formRows.value.splice(i, 1);
};

/** 加载当前生效汇率，并预填录入区（方便在最新版基础上微调另存新版） */
const loadEffective = async () => {
  effective.value = await getEffectiveRatesApi();
  if (effective.value && Object.keys(effective.value.rates).length > 0) {
    formRows.value = Object.entries(effective.value.rates).map(
      ([currency, rate]) => ({ currency, rate })
    );
  } else if (formRows.value.length === 0) {
    addRow();
  }
};

const loadList = async () => {
  listLoading.value = true;
  try {
    list.value =
      (await getExchangeRateListApi(filterCurrency.value || undefined)) ?? [];
  } finally {
    listLoading.value = false;
  }
};

const handleSave = async () => {
  if (!formDate.value || !validRows.value.length) return;
  saving.value = true;
  try {
    const resp = await saveExchangeRateApi(
      formDate.value,
      validRows.value.map((r) => ({
        currency: r.currency,
        rate: Number(r.rate),
      }))
    );
    if (resp !== null) {
      ElMessage.success("汇率快照已保存");
      await loadEffective();
      await loadList();
    }
  } finally {
    saving.value = false;
  }
};

const handleDelete = async (row: ExchangeRateItem) => {
  const resp = await deleteExchangeRateApi(row.id);
  if (resp !== null) {
    ElMessage.success("已删除");
    await loadEffective();
    await loadList();
  }
};

const formatTime = (raw: string): string => {
  if (!raw) return "—";
  return raw.replace("T", " ").slice(0, 19);
};

onMounted(async () => {
  currencyOptions.value = (await getCurrenciesApi()) ?? [];
  await Promise.all([loadEffective(), loadList()]);
});

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_popconfirm = _resolveComponent("el-popconfirm")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_table = _resolveComponent("el-table")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[2] || (_cache[2] = _createElementVNode("div", { class: "er-header__left" }, [
        _createElementVNode("h1", { class: "er-title" }, "汇率管理"),
        _createElementVNode("p", { class: "er-subtitle" }, " 手动维护各币种对 CNY 的汇率快照；页面汇总金额按快照折算，历史月报按月末日汇率计算（数值稳定） ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        (effective.value)
          ? (_openBlock(), _createBlock(_component_el_tag, {
              key: 0,
              type: "success",
              effect: "plain",
              size: "large"
            }, {
              default: _withCtx(() => [
                _createTextVNode(" 当前生效：" + _toDisplayString(effective.value.date) + " 版 ", 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("main", _hoisted_4, [
      _createElementVNode("section", _hoisted_5, [
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "er-card__head" }, [
          _createElementVNode("div", { class: "er-card__title" }, "录入汇率快照"),
          _createElementVNode("div", { class: "er-card__desc" }, " 口径：1 单位外币 = 汇率 CNY（如 1 USD = 7.11 CNY）；CNY 本身无需录入。同日同币种保存时覆盖旧值 ")
        ], -1)),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "er-form__label" }, "生效日期", -1)),
            _createVNode(_component_el_date_picker, {
              modelValue: formDate.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formDate).value = $event)),
              type: "date",
              "value-format": "YYYY-MM-DD",
              placeholder: "选择日期",
              "disabled-date": disableFuture,
              style: {"width":"180px"},
              size: "large"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formRows.value, (row, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: i,
                class: "er-form__row"
              }, [
                _createVNode(_component_el_select, {
                  modelValue: row.currency,
                  "onUpdate:modelValue": ($event: any) => ((row.currency) = $event),
                  placeholder: "币种",
                  size: "large",
                  style: {"width":"200px"}
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currencyOptions.value, (c) => {
                      return (_openBlock(), _createBlock(_component_el_option, {
                        key: c.code,
                        value: c.code,
                        label: `${c.cname} ${c.code} ${c.symbol}`,
                        disabled: isCurrencyUsed(c.code, i)
                      }, null, 8, ["value", "label", "disabled"]))
                    }), 128))
                  ]),
                  _: 2
                }, 1032, ["modelValue", "onUpdate:modelValue"]),
                _createElementVNode("span", _hoisted_9, "1 " + _toDisplayString(row.currency || "?") + " =", 1),
                _createVNode(_component_el_input_number, {
                  modelValue: row.rate,
                  "onUpdate:modelValue": ($event: any) => ((row.rate) = $event),
                  min: 0.00000001,
                  step: 0.01,
                  precision: 6,
                  controls: false,
                  size: "large",
                  placeholder: "对 CNY 汇率",
                  style: {"width":"220px"}
                }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "er-form__unit" }, "CNY", -1)),
                _createVNode(_component_el_button, {
                  icon: _unref(Delete),
                  circle: "",
                  plain: "",
                  type: "danger",
                  size: "small",
                  onClick: ($event: any) => (removeRow(i))
                }, null, 8, ["icon", "onClick"])
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_component_el_button, {
              icon: _unref(Plus),
              plain: "",
              onClick: addRow
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode("添加币种", -1)
              ]))]),
              _: 1
            }, 8, ["icon"]),
            _createVNode(_component_el_button, {
              type: "primary",
              loading: saving.value,
              disabled: !formDate.value || !validRows.value.length,
              onClick: handleSave
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode(" 保存本版汇率 ", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"])
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_11, [
        _createElementVNode("div", _hoisted_12, [
          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "er-card__title" }, "历史快照", -1)),
          _createElementVNode("div", _hoisted_13, [
            _createVNode(_component_el_select, {
              modelValue: filterCurrency.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filterCurrency).value = $event)),
              placeholder: "全部币种",
              clearable: "",
              size: "default",
              style: {"width":"140px"},
              onChange: loadList
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currencyOptions.value, (c) => {
                  return (_openBlock(), _createBlock(_component_el_option, {
                    key: c.code,
                    value: c.code,
                    label: c.code
                  }, null, 8, ["value", "label"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]),
        _withDirectives((_openBlock(), _createBlock(_component_el_table, {
          data: list.value,
          stripe: "",
          style: {"width":"100%"}
        }, {
          empty: _withCtx(() => [
            _createVNode(_component_el_empty, {
              description: "暂无汇率快照，请先录入",
              "image-size": 72
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_table_column, {
              prop: "rateDate",
              label: "生效日期",
              width: "130",
              sortable: ""
            }),
            _createVNode(_component_el_table_column, {
              prop: "currency",
              label: "币种",
              width: "100"
            }),
            _createVNode(_component_el_table_column, {
              label: "汇率（对 CNY）",
              "min-width": "160"
            }, {
              default: _withCtx(({ row }) => [
                _createElementVNode("span", _hoisted_14, "1 " + _toDisplayString(row.currency) + " = " + _toDisplayString(row.rate) + " CNY", 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "updaterId",
              label: "录入人",
              width: "160"
            }),
            _createVNode(_component_el_table_column, {
              label: "更新时间",
              "min-width": "180"
            }, {
              default: _withCtx(({ row }) => [
                _createTextVNode(_toDisplayString(formatTime(row.updatetime || row.createtime)), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              label: "操作",
              width: "90",
              fixed: "right"
            }, {
              default: _withCtx(({ row }) => [
                _createVNode(_component_el_popconfirm, {
                  title: "删除该条汇率快照？",
                  "confirm-button-text": "删除",
                  "cancel-button-text": "取消",
                  onConfirm: ($event: any) => (handleDelete(row))
                }, {
                  reference: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "danger",
                      link: "",
                      size: "small"
                    }, {
                      default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                        _createTextVNode("删除", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["onConfirm"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data"])), [
          [_directive_loading, listLoading.value]
        ])
      ])
    ])
  ]))
}
}

})